/**
 * Gifting Modal JavaScript Module
 *
 * Handles modal behavior, form validation, cart attributes management,
 * and Klaviyo tracking for the gifting functionality.
 *
 * Dependencies: None (vanilla JavaScript)
 * Events: Emits custom events for integration with other components
 */

(function(window, document) {
  'use strict';

  // Module namespace
  const GiftingModal = {

    // Configuration
    config: {
      modalSelector: '.gifting-modal-overlay',
      toggleSelector: '.gifting-toggle',
      formSelector: '[data-gifting-form]',
      applyButtonSelector: '[data-gifting-apply]',
      closeSelectors: '[data-modal-close], .gifting-modal-close',
      backdropSelector: '.gifting-modal-backdrop',
      characterCountSelector: '.gifting-character-count',
      textareaSelector: '.gifting-textarea',
      cartAttributeKey: '_rf_gifting_options',
      hiddenInputName: 'attributes[_rf_gifting_options]'
    },

    // State management
    state: {
      modals: new Map(),
      isInitialized: false
    },

    /**
     * Initialize the gifting modal functionality
     * @param {HTMLElement} container - Optional container to scope initialization
     */
    init(container = document) {
      if (this.state.isInitialized && container === document) {
        return;
      }

      // Find all gifting components
      const components = container.querySelectorAll('.gifting-component');

      components.forEach(component => {
        this.initializeComponent(component);
      });

      // Set up global event listeners
      this.setupGlobalListeners();

      if (container === document) {
        this.state.isInitialized = true;
      }
    },

    /**
     * Initialize a single gifting component
     * @param {HTMLElement} component - The gifting component element
     */
    initializeComponent(component) {
      const productId = component.dataset.giftingProductId;
      if (!productId) return;

      const modal = document.getElementById(`gifting-modal-${productId}`);
      const toggle = component.querySelector(this.config.toggleSelector);

      if (!modal || !toggle) return;

      // Store modal reference
      this.state.modals.set(productId, {
        component,
        modal,
        toggle,
        form: modal.querySelector(this.config.formSelector),
        isOpen: false
      });

      // Set up component-specific listeners
      this.setupModalListeners(productId);
      this.setupFormListeners(productId);

      // Load existing cart attributes
      this.loadExistingData(productId);
    },

    /**
     * Set up modal-specific event listeners
     * @param {string} productId - Product ID
     */
    setupModalListeners(productId) {
      const modalData = this.state.modals.get(productId);
      if (!modalData) return;

      const { modal, toggle } = modalData;

      // Toggle button click
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.openModal(productId);
      });

      // Close button clicks
      modal.querySelectorAll(this.config.closeSelectors).forEach(closeBtn => {
        closeBtn.addEventListener('click', (e) => {
          e.preventDefault();

          // Check if this is a cancel button (secondary button)
          if (closeBtn.classList.contains('gifting-btn--secondary') ||
              closeBtn.textContent.trim().toUpperCase() === 'CANCEL') {
            // Clear form values for cancel button
            this.clearFormValues(productId);
          }

          this.closeModal(productId);
        });
      });

      // Backdrop click
      const backdrop = modal.querySelector(this.config.backdropSelector);
      if (backdrop) {
        backdrop.addEventListener('click', () => {
          this.closeModal(productId);
        });
      }

      // Apply button click
      const applyBtn = modal.querySelector(this.config.applyButtonSelector);
      if (applyBtn) {
        applyBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.applyGiftOptions(productId);
        });
      }

      // Set up swipe gestures for mobile
      this.setupSwipeGestures(productId);
    },

    /**
     * Set up form-specific event listeners
     * @param {string} productId - Product ID
     */
    setupFormListeners(productId) {
      const modalData = this.state.modals.get(productId);
      if (!modalData || !modalData.form) return;

      const { form } = modalData;

      // Form validation
      const inputs = form.querySelectorAll('input[required], textarea[required]');
      inputs.forEach(input => {
        input.addEventListener('blur', () => {
          this.validateField(input);
        });

        input.addEventListener('input', () => {
          // Clear validation state on input
          this.clearFieldValidation(input);
        });
      });
    },

    /**
     * Set up swipe gestures for mobile modal dismissal
     * @param {string} productId - Product ID
     */
    setupSwipeGestures(productId) {
      const modalData = this.state.modals.get(productId);
      if (!modalData) return;

      const { modal } = modalData;
      const container = modal.querySelector('.gifting-modal-container');
      const content = modal.querySelector('.gifting-modal-content');
      
      if (!container || !content) return;

      let startY = 0;
      let currentY = 0;
      let isDragging = false;
      let initialTransform = 0;

      // Touch start
      const handleTouchStart = (e) => {
        // Only handle if starting from top area of modal (handle area)
        const rect = content.getBoundingClientRect();
        const touchY = e.touches[0].clientY;
        
        // Allow swipe from top 60px of modal or from the handle area
        if (touchY - rect.top <= 60) {
          startY = e.touches[0].clientY;
          currentY = startY;
          isDragging = true;
          initialTransform = 0;
          
          // Add transition class for smooth movement
          container.style.transition = 'none';
        }
      };

      // Touch move
      const handleTouchMove = (e) => {
        if (!isDragging) return;

        currentY = e.touches[0].clientY;
        const deltaY = currentY - startY;

        // Only allow downward swipes (positive deltaY)
        if (deltaY > 0) {
          // Prevent scrolling on the background
          e.preventDefault();
          
          // Apply transform with some resistance for better feel
          const resistance = Math.min(deltaY / 3, 150);
          container.style.transform = `translateY(${resistance}px)`;
        }
      };

      // Touch end
      const handleTouchEnd = (e) => {
        if (!isDragging) return;

        isDragging = false;
        const deltaY = currentY - startY;
        
        // Restore transition
        container.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

        // If swiped down enough (more than 100px), close the modal
        if (deltaY > 100) {
          this.closeModal(productId);
        } else {
          // Snap back to original position
          container.style.transform = 'translateY(0)';
        }
      };

      // Mouse events for desktop testing (optional)
      const handleMouseDown = (e) => {
        const rect = content.getBoundingClientRect();
        const mouseY = e.clientY;
        
        if (mouseY - rect.top <= 60) {
          startY = e.clientY;
          currentY = startY;
          isDragging = true;
          initialTransform = 0;
          container.style.transition = 'none';
          
          e.preventDefault();
        }
      };

      const handleMouseMove = (e) => {
        if (!isDragging) return;

        currentY = e.clientY;
        const deltaY = currentY - startY;

        if (deltaY > 0) {
          e.preventDefault();
          const resistance = Math.min(deltaY / 3, 150);
          container.style.transform = `translateY(${resistance}px)`;
        }
      };

      const handleMouseUp = (e) => {
        if (!isDragging) return;

        isDragging = false;
        const deltaY = currentY - startY;
        
        container.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

        if (deltaY > 100) {
          this.closeModal(productId);
        } else {
          container.style.transform = 'translateY(0)';
        }
      };

      // Add event listeners
      content.addEventListener('touchstart', handleTouchStart, { passive: false });
      modal.addEventListener('touchmove', handleTouchMove, { passive: false });
      modal.addEventListener('touchend', handleTouchEnd, { passive: true });

      // Only add mouse events on touch devices or for development
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      if (isTouchDevice) {
        content.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      }

      // Store cleanup function
      modalData.cleanupSwipe = () => {
        content.removeEventListener('touchstart', handleTouchStart);
        modal.removeEventListener('touchmove', handleTouchMove);
        modal.removeEventListener('touchend', handleTouchEnd);
        
        if (isTouchDevice) {
          content.removeEventListener('mousedown', handleMouseDown);
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
        }
      };
    },

    /**
     * Set up global event listeners
     */
    setupGlobalListeners() {
      // Escape key to close modals
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeAllModals();
        }
      });

      // Listen for cart form submissions to sync attributes
      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (form.matches('form[action*="/cart/add"], .product__form, .product-card__add-to-cart--form')) {
          this.handleCartSubmission(form);
        }
      });

      // Listen for custom add to cart events
      document.addEventListener('addToCartCustom', (e) => {
        this.handleCustomCartEvent(e);
      });
    },

    /**
     * Open a modal
     * @param {string} productId - Product ID
     */
    openModal(productId) {
      const modalData = this.state.modals.get(productId);
      if (!modalData || modalData.isOpen) return;

      const { modal, toggle } = modalData;
      const container = modal.querySelector('.gifting-modal-container');

      // Update state
      modalData.isOpen = true;

      // Check if we're on mobile or desktop
      const isMobile = window.innerWidth <= 640;

      // Set initial state for animation
      if (container) {
        container.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        
        if (isMobile) {
          // Mobile: start from bottom
          container.style.transform = 'translateY(100%)';
        } else {
          // Desktop: start slightly below with fade
          container.style.transform = 'translateY(20px)';
        }
      }

      // Update DOM
      modal.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-pressed', 'true');

      // Trigger animation after a brief delay to ensure proper rendering
      setTimeout(() => {
        if (container && modalData.isOpen) {
          container.style.transform = 'translateY(0)';
        }
      }, 10);

      // Focus management
      const firstInput = modal.querySelector('input, textarea, button');
      if (firstInput) {
        setTimeout(() => firstInput.focus(), 350);
      }

      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      // Emit custom event
      this.emitEvent('giftingModalOpened', { productId });

      // Track with Klaviyo
      this.trackKlaviyoEvent('Gift Modal Opened', { productId });
    },

    /**
     * Close a modal
     * @param {string} productId - Product ID
     */
    closeModal(productId) {
      const modalData = this.state.modals.get(productId);
      if (!modalData || !modalData.isOpen) return;

      const { modal, toggle } = modalData;
      const container = modal.querySelector('.gifting-modal-container');

      // Update state
      modalData.isOpen = false;

      // Check if we're on mobile or desktop
      const isMobile = window.innerWidth <= 640;

      // Set appropriate close animation based on device
      if (container) {
        container.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        
        if (isMobile) {
          // Mobile: slide down
          container.style.transform = 'translateY(100%)';
        } else {
          // Desktop: fade and slight drop
          container.style.transform = 'translateY(20px)';
        }
        
        // Wait for animation before hiding
        setTimeout(() => {
          if (!modalData.isOpen) { // Double check modal is still closed
            modal.setAttribute('aria-hidden', 'true');
            container.style.transform = '';
            container.style.transition = '';
          }
        }, 300);
      } else {
        modal.setAttribute('aria-hidden', 'true');
      }

      toggle.setAttribute('aria-pressed', 'false');

      // Restore body scroll
      document.body.style.overflow = '';

      // Return focus to toggle
      toggle.focus();

      // Cleanup swipe gestures
      if (modalData.cleanupSwipe) {
        modalData.cleanupSwipe();
      }

      // Emit custom event
      this.emitEvent('giftingModalClosed', { productId });
    },

    /**
     * Close all open modals
     */
    closeAllModals() {
      this.state.modals.forEach((modalData, productId) => {
        if (modalData.isOpen) {
          this.closeModal(productId);
        }
      });
    },

    /**
     * Apply gift options and close modal
     * @param {string} productId - Product ID
     */
    applyGiftOptions(productId) {
      const modalData = this.state.modals.get(productId);
      if (!modalData || !modalData.modal) {
        console.error('GiftingModal: No modal data found for product', productId);
        return;
      }

      // Get inputs directly from modal body instead of relying on form
      const modalBody = modalData.modal.querySelector('.gifting-modal-body');
      if (!modalBody) {
        console.error('GiftingModal: No modal body found');
        return;
      }

      // Collect input data directly from modal body
      const nameInput = modalBody.querySelector('input[name="recipient_name"]');
      const emailInput = modalBody.querySelector('input[name="recipient_email"]');
      const messageInput = modalBody.querySelector('textarea[name="gift_message"]');
      const creditInput = modalBody.querySelector('input[name="product_credit"]');

      const name = nameInput ? nameInput.value.trim() : '';
      const email = emailInput ? emailInput.value.trim() : '';
      const message = messageInput ? messageInput.value.trim() : '';
      const credit = creditInput ? parseFloat(creditInput.value) || 0 : 0;

      // Basic validation - require name and email
      if (!name || !email) {
        console.log('GiftingModal: Missing required fields');
        // Show validation errors
        if (!name && nameInput) {
          this.validateField(nameInput);
        }
        if (!email && emailInput) {
          this.validateField(emailInput);
        }
        return;
      }

      // Create the gifting data object
      const giftingData = {
        name: name,
        email: email,
        message: message,
        credit: credit
      };

      // Convert to JSON string
      const jsonData = JSON.stringify(giftingData);

      // Save to cart attributes using Shopify Cart API
      this.saveCartAttribute(jsonData);

      // Update global variable for other scripts
      window._rf_gifting_options = jsonData;

      // Update toggle visual state
      this.updateToggleState(productId, true);

      // Close modal
      this.closeModal(productId);

      // Emit custom event
      this.emitEvent('giftingOptionsApplied', { productId, formData: giftingData });

      // Track with Klaviyo
      this.trackKlaviyoEvent('Gift Options Applied', giftingData);
    },

    /**
     * Save cart attribute using Shopify Cart API
     * @param {string} jsonData - JSON string of gifting data
     */
    saveCartAttribute(jsonData) {
      const attributeData = {
        attributes: {
          '_rf_gifting_options': jsonData
        }
      };

      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(attributeData)
      })
      .then(response => response.json())
      .then(data => {
        console.log('GiftingModal: Cart attribute saved successfully', data);
      })
      .catch(error => {
        console.error('GiftingModal: Error saving cart attribute:', error);
      });
    },

    /**
     * Clear cart attribute using Shopify Cart API
     */
    clearCartAttributeFromCart() {
      const attributeData = {
        attributes: {
          '_rf_gifting_options': null
        }
      };

      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(attributeData)
      })
      .then(response => response.json())
      .then(data => {
        console.log('GiftingModal: Cart attribute cleared successfully', data);
      })
      .catch(error => {
        console.error('GiftingModal: Error clearing cart attribute:', error);
      });
    },

    /**
     * Validate form fields
     * @param {HTMLElement} form - Form element
     * @returns {boolean} - Validation result
     */
    validateForm(form) {
      const requiredFields = form.querySelectorAll('input[required], textarea[required]');
      let isValid = true;

      requiredFields.forEach(field => {
        if (!this.validateField(field)) {
          isValid = false;
        }
      });

      return isValid;
    },

    /**
     * Validate individual field
     * @param {HTMLElement} field - Input field
     * @returns {boolean} - Validation result
     */
    validateField(field) {
      const value = field.value.trim();
      const isValid = field.checkValidity() && value.length > 0;

      if (!isValid) {
        field.classList.add('gifting-field-error');
        field.setAttribute('aria-invalid', 'true');
      } else {
        this.clearFieldValidation(field);
      }

      return isValid;
    },

    /**
     * Clear field validation state
     * @param {HTMLElement} field - Input field
     */
    clearFieldValidation(field) {
      field.classList.remove('gifting-field-error');
      field.removeAttribute('aria-invalid');
    },

    /**
     * Clear form values
     * @param {string} productId - Product ID
     */
    clearFormValues(productId) {
      const modalData = this.state.modals.get(productId);
      if (!modalData || !modalData.modal) return;

      const modalBody = modalData.modal.querySelector('.gifting-modal-body');
      if (!modalBody) return;

      // Clear all form fields from modal body
      const nameField = modalBody.querySelector('input[name="recipient_name"]');
      const emailField = modalBody.querySelector('input[name="recipient_email"]');
      const messageField = modalBody.querySelector('textarea[name="gift_message"]');

      if (nameField) {
        nameField.value = '';
        this.clearFieldValidation(nameField);
      }
      if (emailField) {
        emailField.value = '';
        this.clearFieldValidation(emailField);
      }
      if (messageField) {
        messageField.value = '';
        this.clearFieldValidation(messageField);
      }

      // Clear cart attribute using Shopify Cart API
      this.clearCartAttributeFromCart();

      // Clear global variable
      window._rf_gifting_options = '';

      // Update toggle visual state
      this.updateToggleState(productId, false);
    },

    /**
     * Clear cart attributes completely
     * @param {string} productId - Product ID
     */
    clearCartAttributes(productId) {
      // This method is kept for backward compatibility
      // The actual clearing is now done via clearCartAttributeFromCart()
      // which uses the Shopify Cart API
      console.log('GiftingModal: clearCartAttributes called (deprecated - use clearCartAttributeFromCart)');
    },

    /**
     * Collect form data
     * @param {HTMLElement} form - Form element
     * @returns {Object} - Form data object
     */
    collectFormData(form) {
      const formData = new FormData(form);

      return {
        name: formData.get('recipient_name') || '',
        email: formData.get('recipient_email') || '',
        message: formData.get('gift_message') || '',
        credit: parseFloat(formData.get('product_credit')) || 0,
        productId: formData.get('product_id') || ''
      };
    },

    /**
     * Save data to cart attributes
     * @param {Object} data - Gift data
     */
    saveToCartAttributes(data) {
      // Create the JSON string with the exact structure needed
      const giftingData = {
        name: data.name || '',
        email: data.email || '',
        message: data.message || '',
        credit: data.credit || 0
      };

      const jsonData = JSON.stringify(giftingData);

      // Save to cart attributes using Shopify Cart API
      this.saveCartAttribute(jsonData);

      // Update global variable for other scripts
      window._rf_gifting_options = jsonData;
    },

    /**
     * Load existing cart attribute data
     * @param {string} productId - Product ID
     */
    loadExistingData(productId) {
      console.log('GiftingModal: Loading existing data for product', productId);

      // Try to get existing data from multiple sources
      let existingData = null;

      // First try the gifting component's hidden input
      const giftingComponent = document.querySelector(`[data-gifting-product-id="${productId}"]`);
      if (giftingComponent) {
        const existingInput = giftingComponent.querySelector('.gifting-json');
        if (existingInput && existingInput.value && existingInput.value !== '""' && existingInput.value !== 'null') {
          try {
            console.log('GiftingModal: Found hidden input value:', existingInput.value);
            // Handle case where value might be double-encoded JSON from Liquid
            let rawValue = existingInput.value;
            if (rawValue.startsWith('"') && rawValue.endsWith('"')) {
              rawValue = JSON.parse(rawValue);
            }
            existingData = JSON.parse(rawValue);
            console.log('GiftingModal: Parsed existing data:', existingData);
          } catch (e) {
            console.error('GiftingModal: Error parsing hidden input value:', e, existingInput.value);
          }
        }
      }

      // If not found, try global variable
      if (!existingData) {
        try {
          console.log('GiftingModal: Checking global giftingConfig variable');
          if (window.giftingConfig) {
            existingData = window.giftingConfig;
            // Update the hidden input with this data
            if (giftingComponent) {
              const giftingDataComponent = giftingComponent.querySelector(".gifting-json");
              if (giftingDataComponent) {
                giftingDataComponent.value = JSON.stringify(existingData);
              }
            }
            console.log('GiftingModal: Loaded from global giftingConfig:', existingData);
          }
        } catch (e) {
          console.error('GiftingModal: Error with global giftingConfig:', e);
        }
      }

      // If not found, try cart form inputs
      if (!existingData) {
        const cartForms = document.querySelectorAll('form[action*="/cart/add"], .product__form, .product-card__add-to-cart--form');
        for (const form of cartForms) {
          const hiddenInput = form.querySelector(`input[name="${this.config.hiddenInputName}"]`);
          if (hiddenInput && hiddenInput.value && hiddenInput.value !== '""' && hiddenInput.value !== 'null') {
            try {
              let rawValue = hiddenInput.value;
              if (rawValue.startsWith('"') && rawValue.endsWith('"')) {
                rawValue = JSON.parse(rawValue);
              }
              existingData = JSON.parse(rawValue);
              console.log('GiftingModal: Loaded from cart form:', existingData);
              break;
            } catch (e) {
              console.error('GiftingModal: Error parsing cart form input:', e);
              // Continue to next form
            }
          }
        }
      }

      // If we have valid data with at least one field populated, populate the form
      if (existingData && typeof existingData === 'object' &&
          (existingData.name || existingData.email || existingData.message || existingData.credit)) {
        console.log('GiftingModal: Populating form with existing data:', existingData);
        this.populateForm(productId, existingData);
        this.updateToggleState(productId, true);
      } else {
        console.log('GiftingModal: No valid existing data found for product', productId);
      }
    },

    /**
     * Populate form with existing data
     * @param {string} productId - Product ID
     * @param {Object} data - Gift data
     */
    populateForm(productId, data) {
      const modalData = this.state.modals.get(productId);
      if (!modalData || !modalData.modal) return;

      // Look for fields in modal body (consistent with applyGiftOptions method)
      const modalBody = modalData.modal.querySelector('.gifting-modal-body');
      if (!modalBody) return;

      // Populate fields
      const nameField = modalBody.querySelector('input[name="recipient_name"]');
      const emailField = modalBody.querySelector('input[name="recipient_email"]');
      const messageField = modalBody.querySelector('textarea[name="gift_message"]');

      if (nameField && data.name) {
        nameField.value = data.name;
      }
      if (emailField && data.email) {
        emailField.value = data.email;
      }
      if (messageField && data.message) {
        messageField.value = data.message;
      }
    },

    /**
     * Update toggle visual state
     * @param {string} productId - Product ID
     * @param {boolean} hasGiftData - Whether gift data exists
     */
    updateToggleState(productId, hasGiftData) {
      const modalData = this.state.modals.get(productId);
      if (!modalData) return;

      const { toggle } = modalData;
      const label = toggle.querySelector('.gifting-label');

      if (hasGiftData) {
        toggle.classList.add('gifting-toggle--active');
        if (label) {
          label.textContent = 'Gift options added ✓';
        }
      } else {
        toggle.classList.remove('gifting-toggle--active');
        if (label) {
          label.textContent = 'Send as a gift';
        }
      }
    },

    /**
     * Handle cart form submission
     * @param {HTMLElement} form - Cart form element
     */
    handleCartSubmission(form) {
      const giftingInput = form.querySelector(`input[name="${this.config.hiddenInputName}"]`);

      if (giftingInput && giftingInput.value) {
        try {
          const giftData = JSON.parse(giftingInput.value);
          this.trackKlaviyoEvent('Gift Added to Cart', giftData);
        } catch (e) {
          // Ignore parsing errors
        }
      }
    },

    /**
     * Handle custom cart events
     * @param {CustomEvent} event - Custom cart event
     */
    handleCustomCartEvent(event) {
      const props = event.detail && event.detail.properties;

      if (props && props[this.config.cartAttributeKey]) {
        try {
          const giftData = typeof props[this.config.cartAttributeKey] === 'string'
            ? JSON.parse(props[this.config.cartAttributeKey])
            : props[this.config.cartAttributeKey];

          this.trackKlaviyoEvent('Gift Added to Cart', giftData);
        } catch (e) {
          // Ignore parsing errors
        }
      }
    },

    /**
     * Track Klaviyo events
     * @param {string} eventName - Event name
     * @param {Object} data - Event data
     */
    trackKlaviyoEvent(eventName, data) {
      try {
        if (window._learnq && typeof window._learnq.push === 'function') {
          window._learnq.push(['track', eventName, data]);
        } else if (window.Klaviyo && typeof window.Klaviyo.track === 'function') {
          window.Klaviyo.track(eventName, data);
        }
      } catch (e) {
        // Non-critical error, continue execution
      }
    },

    /**
     * Emit custom events
     * @param {string} eventName - Event name
     * @param {Object} detail - Event detail
     */
    emitEvent(eventName, detail) {
      const event = new CustomEvent(eventName, {
        detail,
        bubbles: true,
        cancelable: true
      });

      document.dispatchEvent(event);
    },

    /**
     * Public API for manual control
     */
    api: {
      open: (productId) => GiftingModal.openModal(productId),
      close: (productId) => GiftingModal.closeModal(productId),
      closeAll: () => GiftingModal.closeAllModals(),
      clear: (productId) => GiftingModal.clearFormValues(productId),
      reinitialize: (container) => GiftingModal.init(container)
    }
  };

  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => GiftingModal.init());
  } else {
    GiftingModal.init();
  }

  // Expose public API
  window.GiftingModal = GiftingModal.api;

})(window, document);